import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, ChevronDown, Timer, ShieldCheck, Gift, X } from "lucide-react";
import { useLang } from "../i18n";
import { api, LOGO_URL } from "../lib/api";
import { LEGAL_DOCS } from "../constants/legalDocs";

export const CraftLogo = ({ size = 44 }) => (
  <div className="flex items-center gap-2" data-testid="craft-logo">
    <img src={LOGO_URL} alt="CRAFT" style={{ width: size, height: size }} className="rounded-2xl object-cover" />
    <div className="leading-none">
      <div className="font-display font-black text-lg tracking-tight">CRAFT</div>
      <div className="text-[10px] font-bold tracking-[0.2em] text-gray-500 uppercase">fast food</div>
    </div>
  </div>
);

export const LangSwitcher = () => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const names = { ru: "Русский", uz: "O'zbekcha", en: "English" };
  return (
    <div className="relative">
      <button data-testid="lang-switcher" onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 bg-white rounded-full px-3.5 py-2 text-sm font-bold border border-gray-100 shadow-sm">
        <Globe size={16} className="text-gray-600" /> {names[lang]} <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 min-w-[140px]">
          {Object.entries(names).map(([k, v]) => (
            <button key={k} data-testid={`lang-option-${k}`} onClick={() => { setLang(k); setOpen(false); }}
              className={`block w-full text-left px-4 py-2.5 text-sm font-semibold hover:bg-yellow-50 ${k === lang ? "text-[#FF0000]" : ""}`}>
              {v}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const BannerCard = ({ ad, campaignId, compact }) => {
  const { L } = useLang();
  useEffect(() => {
    if (ad) api.post(`/public/banner/${ad.id}/impression`, {}).catch(() => {});
  }, [ad]);
  if (!ad) return null;
  const click = () => {
    api.post(`/public/banner/${ad.id}/click`, { campaign_id: campaignId }).catch(() => {});
    if (ad.link) window.open(ad.link, "_blank");
  };
  const dark = ad.dark;
  if (ad.photo_only) {
    return (
      <button data-testid={`banner-${ad.id}`} onClick={click} aria-label={ad.name || "banner"}
        className={`relative block w-full overflow-hidden rounded-3xl transition-transform active:scale-[0.98] ${compact ? "h-36" : "h-48"}`}>
        <img src={ad.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
      </button>
    );
  }
  return (
    <button data-testid={`banner-${ad.id}`} onClick={click} aria-label={L(ad.title)}
      className={`relative block w-full overflow-hidden rounded-3xl text-left shadow-lg transition-transform active:scale-[0.98] hover:brightness-110 ${compact ? "h-32" : "h-44"} ${dark ? "bg-[#111111]" : "bg-[#FEBC10]"}`}>
      {ad.image_url && <img src={ad.image_url} alt="" className="absolute right-0 top-0 h-full w-[58%] object-cover" />}
      <div className={`absolute inset-0 ${dark ? "bg-gradient-to-r from-[#111111] from-40% via-[#111111]/85 to-transparent" : "bg-gradient-to-r from-[#FEBC10] from-40% via-[#FEBC10]/85 to-transparent"}`} />
      <div className="absolute inset-y-0 left-0 w-[62%] p-5 flex flex-col justify-center gap-1">
        <div className={`font-display font-black uppercase leading-tight ${compact ? "text-base" : "text-lg"} ${dark ? "text-white" : "text-black"}`}>{L(ad.title)}</div>
        <div className={`text-xs font-semibold ${dark ? "text-gray-300" : "text-black/70"}`}>{L(ad.description)}</div>
        {ad.tag && <span className="self-start bg-[#FF0000] text-white font-display font-black px-3 py-1 rounded-lg text-sm mt-1">{ad.tag}</span>}
      </div>
    </button>
  );
};

export const LabeledStepper = ({ steps, activeIndex }) => {
  const { t } = useLang();
  const LBL = { identify: "lbl_phone", form: "lbl_info", question: "lbl_survey", advertisement: "lbl_bonus", review: "lbl_review", custom_text: "lbl_info" };
  const items = steps.filter((s) => s.type !== "welcome").map((s) => t(LBL[s.type] || "lbl_info"));
  items.push(t("lbl_done"));
  return (
    <div className="flex items-start justify-between gap-1 my-4" data-testid="labeled-stepper">
      {items.map((label, i) => (
        <div key={i} className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black transition-colors ${i < activeIndex ? "bg-[#FEBC10]/40 text-black" : i === activeIndex ? "bg-[#FEBC10] text-black shadow-[0_3px_8px_rgba(254,188,16,0.5)]" : "bg-gray-100 text-gray-400"}`}>{i + 1}</div>
          <span className={`text-[9px] font-bold text-center leading-tight truncate w-full ${i === activeIndex ? "text-black" : "text-gray-400"}`}>{label}</span>
        </div>
      ))}
    </div>
  );
};

export const FeatureRow = () => {
  const { t } = useLang();
  const items = [[Timer, "feat_fast_t", "feat_fast_d"], [ShieldCheck, "feat_safe_t", "feat_safe_d"], [Gift, "feat_deal_t", "feat_deal_d"]];
  return (
    <div className="grid grid-cols-3 gap-3 mt-5 craft-card p-5" data-testid="feature-row">
      {items.map(([Icon, tt, dd]) => (
        <div key={tt} className="flex flex-col items-center text-center gap-1.5">
          <Icon size={26} strokeWidth={2.5} className="text-[#EFAF04]" />
          <div className="text-xs font-display font-black uppercase">{t(tt)}</div>
          <div className="text-[10px] text-gray-500 font-semibold leading-tight">{t(dd)}</div>
        </div>
      ))}
    </div>
  );
};

export const ProgressSteps = ({ current, total }) => {
  const { t } = useLang();
  return (
    <div className="mb-5" data-testid="progress-indicator">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-extrabold uppercase tracking-wider text-gray-400">{t("step_of", { a: current + 1, b: total })}</span>
        <span className="text-xs font-black text-[#FF0000]">{Math.round(((current + 1) / total) * 100)}%</span>
      </div>
      <div className="flex gap-1.5">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-300 ${i <= current ? "bg-[#FEBC10]" : "bg-gray-200"}`} />
        ))}
      </div>
    </div>
  );
};

export const CraftFooter = () => {
  const { t, L } = useLang();
  const [openDoc, setOpenDoc] = useState(null);
  const links = [
    ["privacy", "privacy"],
    ["data_consent", "data-consent"],
    ["offer", "offer"],
    ["terms", "terms"],
  ];
  const doc = openDoc ? LEGAL_DOCS[openDoc] : null;
  return (
    <footer className="mt-8 px-5 pb-8" data-testid="craft-footer">
      <div className="border-t border-gray-200 pt-5 flex flex-col gap-2">
        {links.map(([key, slug]) => (
          <button key={slug} type="button" data-testid={`footer-link-${slug}`} onClick={() => setOpenDoc(slug)}
            className="text-xs font-semibold text-gray-500 hover:text-black text-left">
            {t(key)}
          </button>
        ))}
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs font-extrabold">CRAFT <span className="text-[#FF0000]">fast food</span></span>
          <a data-testid="footer-uzbiznes" href="https://uzbiznes.com" target="_blank" rel="noreferrer" className="text-[10px] text-gray-300 hover:text-gray-500">
            {t("developed")}
          </a>
        </div>
        <span className="text-[10px] text-gray-400">© CRAFT fast food</span>
      </div>
      {doc && (
        <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4" data-testid="legal-modal"
          onClick={() => setOpenDoc(null)}>
          <div className="bg-white w-full max-w-[430px] rounded-t-3xl sm:rounded-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 p-5 pb-3 border-b border-gray-100">
              <h2 className="font-display font-black text-lg leading-tight" data-testid="legal-modal-title">{L(doc.title)}</h2>
              <button type="button" data-testid="legal-modal-close" onClick={() => setOpenDoc(null)}
                className="w-9 h-9 shrink-0 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center" aria-label="Close">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{L(doc.body)}</p>
            </div>
            <div className="p-4 pt-0">
              <button type="button" data-testid="legal-modal-close-btn" onClick={() => setOpenDoc(null)} className="craft-btn">{t("close")}</button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};
